(() => {
  if (window.__stockBadgeInit) return;
  window.__stockBadgeInit = true;

  const SELECTORS = {
    scope: 'product-info',
    badge: '[data-stock-badge]',
    variantInput: 'form[action^="/cart/add"] input[name="id"]',
    variantSelect: 'form[action^="/cart/add"] select[name="id"], select[name="id"]',
    optionInput: 'variant-selects input, variant-selects select',
    mapPrefix: 'variant-inventory-'
  };

  const cache = new Map();

  let rafId = null;
  let retryTimer = null;
  let retryCount = 0;
  let lastVariantIdFromEvent = null;
  let lastSuccessfulState = null;

  const $ = (selector, root = document) => root.querySelector(selector);

  const getScope = () => document.querySelector(SELECTORS.scope) || document;

  const getProductId = () => {
    const el = document.querySelector(`script[id^="${SELECTORS.mapPrefix}"]`);
    return el ? el.id.replace(SELECTORS.mapPrefix, '') : null;
  };

  const getInventoryMap = (productId) => {
    if (!productId) return null;
    if (cache.has(productId)) return cache.get(productId);

    const el = document.getElementById(`${SELECTORS.mapPrefix}${productId}`);
    if (!el) return null;

    try {
      const map = JSON.parse(el.textContent || '{}');
      cache.set(productId, map);
      return map;
    } catch (error) {
      return null;
    }
  };

  const getCandidateVariantIds = (root) => {
    const checkedOptions = Array.from(
      root.querySelectorAll('variant-selects input:checked')
    );

    return [
      lastVariantIdFromEvent,
      $(SELECTORS.variantInput, root)?.value,
      $(SELECTORS.variantSelect, root)?.value,
      ...checkedOptions.map((option) => option?.dataset?.variantId)
    ].filter(Boolean);
  };

  const getSelectedVariantId = (root, map) => {
    const candidates = getCandidateVariantIds(root);

    if (map) {
      const matchedId = candidates.find((id) => map[String(id)]);
      if (matchedId) return matchedId;
    }

    return candidates[0] || null;
  };

  const readBadgeText = (badge, datasetKey, attrName, fallback = '') => {
    const datasetValue = badge.dataset?.[datasetKey];
    if (datasetValue && datasetValue.trim()) return datasetValue.trim();

    const attrValue = badge.getAttribute(attrName);
    if (attrValue && attrValue.trim()) return attrValue.trim();

    return fallback;
  };

  const ensureMarkup = (badge) => {
    const existingText = badge.querySelector('[data-stock-text]');

    if (existingText) {
      badge.__ohTextEl = existingText;
      return;
    }

    badge.innerHTML = `
      <span class="stock-badge__icon" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" focusable="false">
          <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </span>
      <span class="stock-badge__text" data-stock-text></span>
    `;

    badge.__ohTextEl = badge.querySelector('[data-stock-text]');
  };

  const isInStock = (data) => {
    if (!data || !data.available) return false;

    const managed = data.managed;
    const qty = Number(data.qty || 0);

    if (!managed) return true;

    return qty > 0;
  };

  const isExpress = (data) => {
    return Boolean(
      data?.express_shipping ||
      data?.expressShipping ||
      data?.is_express_shipping ||
      data?.isExpressShipping ||
      data?.express
    );
  };

  const applyState = (badge, state) => {
    if (!state) return;

    ensureMarkup(badge);

    badge.hidden = false;

    badge.classList.toggle('stock-badge--in', state.inStock);
    badge.classList.toggle('stock-badge--out', !state.inStock);
    badge.classList.toggle('stock-badge--express', state.express);

    if (badge.__ohTextEl && badge.__ohTextEl.textContent !== state.text) {
      badge.__ohTextEl.textContent = state.text;
    }
  };

  const buildState = (badge, data) => {
    const inStockText = readBadgeText(
      badge,
      'inStockText',
      'data-in-stock-text',
      'In Stock'
    );

    const outOfStockText = readBadgeText(
      badge,
      'outOfStockText',
      'data-out-of-stock-text',
      'Out of Stock'
    );

    const expressText = readBadgeText(
      badge,
      'expressShippingInStockText',
      'data-express-shipping-in-stock-text',
      inStockText
    );

    const inStock = isInStock(data);
    const express = inStock && isExpress(data);
    const text = inStock ? (express ? expressText : inStockText) : outOfStockText;

    return {
      inStock,
      express,
      text
    };
  };

  const retryUpdate = () => {
    clearTimeout(retryTimer);

    if (retryCount >= 3) {
      retryCount = 0;

      const badge = $(SELECTORS.badge, getScope());
      if (badge && lastSuccessfulState) {
        applyState(badge, lastSuccessfulState);
      }

      return;
    }

    retryCount += 1;
    retryTimer = setTimeout(updateBadge, 80);
  };

  const updateBadge = () => {
    rafId = null;

    const scope = getScope();
    const badge = $(SELECTORS.badge, scope);
    if (!badge) return;

    ensureMarkup(badge);

    const productId = getProductId();
    const map = getInventoryMap(productId);
    const variantId = getSelectedVariantId(scope, map);
    const data = map?.[String(variantId)];

    if (!data) {
      retryUpdate();
      return;
    }

    retryCount = 0;
    clearTimeout(retryTimer);

    const state = buildState(badge, data);
    lastSuccessfulState = state;

    applyState(badge, state);
  };

  const scheduleUpdate = () => {
    if (rafId) cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(updateBadge);
    });
  };

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!target) return;

      if (
        target.matches(SELECTORS.variantInput) ||
        target.matches(SELECTORS.variantSelect) ||
        target.matches(SELECTORS.optionInput)
      ) {
        scheduleUpdate();
        setTimeout(scheduleUpdate, 120);
      }
    },
    { passive: true }
  );

  document.addEventListener(
    'variant:change',
    (event) => {
      lastVariantIdFromEvent =
        event.detail?.variant?.id ||
        event.detail?.id ||
        lastVariantIdFromEvent;

      scheduleUpdate();
      setTimeout(scheduleUpdate, 120);
    },
    { passive: true }
  );

  document.addEventListener(
    'variant:changed',
    (event) => {
      lastVariantIdFromEvent =
        event.detail?.variant?.id ||
        event.detail?.id ||
        lastVariantIdFromEvent;

      scheduleUpdate();
      setTimeout(scheduleUpdate, 120);
    },
    { passive: true }
  );

  const observeVariantInput = () => {
    const scope = getScope();
    const input = $(SELECTORS.variantInput, scope);

    if (!input || input.__ohStockObserved) return;

    input.__ohStockObserved = true;

    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(input, {
      attributes: true,
      attributeFilter: ['value']
    });
  };

  const observeProductInfo = () => {
    const scope = getScope();

    if (!scope || scope.__ohStockProductObserved) return;

    scope.__ohStockProductObserved = true;

    const observer = new MutationObserver(() => {
      observeVariantInput();
      scheduleUpdate();
    });

    observer.observe(scope, {
      childList: true,
      subtree: true
    });
  };

  document.addEventListener('shopify:section:load', () => {
    observeVariantInput();
    observeProductInfo();
    scheduleUpdate();
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        observeVariantInput();
        observeProductInfo();
        scheduleUpdate();
      },
      { once: true }
    );
  } else {
    observeVariantInput();
    observeProductInfo();
    scheduleUpdate();
  }
})();